import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useDrag, useDrop, DragElementWrapper } from "react-dnd";
import classnames from "classnames";

import styles from "./blending.module.css";
import * as blendingActions from "../../redux/actions/blending";
import { srcPath } from "../../util/Path";
import { dndItemTypes } from "../../react-dnd/DndConfig";
import { State } from "../../redux/store";
import { displayLetter } from "../../util/Util";

interface Props {
  alphabet: string;
  onBoard: boolean;
  index: number;
  style?: any;
}

interface Item {
  type: string;
  onBoard: boolean;
  index: number;
  alphabet: string;
}

interface Filled {
  dragRef: DragElementWrapper<any>;
  canDrag: boolean;
  isDragging: boolean;
  alphabet: string;
  style?: any;
}

export const LetterCardFilled = (props: Filled) => {
  const { dragRef, canDrag, isDragging, alphabet } = props;
  const { isSingleRow } = useSelector((state: State) => state.blending);
  let { style } = props;
  const displayAlphabet = displayLetter(alphabet);
  const abnormalCard = !canDrag && alphabet.includes("#");

  if (abnormalCard) {
    style = style || {};
    style.width = `${displayAlphabet.length * 45 + 20}px`;
    style.letterSpacing = "5px";
  }

  return (
    <div
      ref={dragRef}
      className={styles.wordCardContainer}
      style={{
        cursor: canDrag ? "move" : "unset",
        height: `${isSingleRow ? 220 : 170}px`,
        ...style,
      }}
    >
      {/* <img
        className={styles.wordCard}
        src={srcPath("blending/word_card.png")}
        alt=""
        style={{
          opacity: canDrag || isDragging ? 1 : 0.7,
        }}
      /> */}
      {alphabet && (
        <>
          <img
            className={classnames(styles.wordCardIcon)}
            style={{
              top: `${isSingleRow ? 20 : 5}px`,
            }}
            src={srcPath(
              `blending/alphabets/icon_${alphabet.toLowerCase()}.png`
            )}
            alt=""
          />
          <div
            className={classnames("noselect", styles.wordCardAlphabet)}
            style={{
              fontSize: abnormalCard
                ? "65px"
                : `${80 - (displayAlphabet.length - 1) * 15}px`,
              bottom: abnormalCard
                ? `${30 - (isSingleRow ? 0 : 15)}px`
                : `${
                    25 +
                    (displayAlphabet.length - 1) * 5 -
                    (isSingleRow ? 0 : 15)
                  }px`,
            }}
          >
            {displayAlphabet}
          </div>
        </>
      )}
    </div>
  );
};

const LetterCard = (props: Props) => {
  const { alphabet, onBoard, index, style } = props;
  const dispatch = useDispatch();
  const { blanks, isSingleRow } = useSelector((state: State) => state.blending);

  const dragCard = (item: Item) => {
    dispatch(
      blendingActions.dragCard(
        item.alphabet,
        item.onBoard,
        item.index,
        onBoard,
        index
      )
    );
    dispatch(blendingActions.checkAnswer());
  };

  const [{ isDragging }, drag] = useDrag({
    item: {
      type: onBoard
        ? dndItemTypes.BLENDING_ONBOARD_LETTER_CARD
        : dndItemTypes.BLENDING_LETTER_CARD,
      onBoard,
      index,
      alphabet,
    },
    canDrag: () => !onBoard || blanks.includes(index),
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: onBoard
      ? [
          dndItemTypes.BLENDING_ONBOARD_LETTER_CARD,
          dndItemTypes.BLENDING_LETTER_CARD,
        ]
      : dndItemTypes.BLENDING_ONBOARD_LETTER_CARD,
    drop: dragCard,
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  });

  // drag
  if (alphabet && !isDragging) {
    return (
      <LetterCardFilled
        dragRef={drag}
        canDrag={!onBoard || blanks.includes(index)}
        isDragging={false}
        alphabet={alphabet}
        style={style}
      />
    );
  }

  let dropOpacity: number = 0.7;
  if (isOver) dropOpacity = 0.1;
  else if ((canDrop && !isDragging) || isDragging) dropOpacity = 0.3;

  // drop
  return (
    <div
      ref={drop}
      className={styles.wordCardContainer}
      style={{
        zIndex: 10, // must be larger than drag item
        opacity: dropOpacity,
        height: `${isSingleRow ? 220 : 170}px`,
        backgroundColor:
          canDrop && !isDragging
            ? "rgba(0, 255, 0, 0.9)"
            : "rgba(255, 255, 255, 0.2)",
        ...style,
      }}
    >
      {/* <img
        className={styles.wordCard}
        src={srcPath("blending/placeholder.png")}
        alt=""
      /> */}
    </div>
  );
};

export default LetterCard;
